import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Lock, Key, Shield, Users, FileText, Eye } from 'lucide-react';

const ZohoVault = () => {
  const features = [
    {
      icon: <Lock className="w-6 h-6 text-blue-700" />,
      title: 'Veilige Opslag',
      description: 'Militaire encryptie voor al uw wachtwoorden en gevoelige data.',
    },
    {
      icon: <Users className="w-6 h-6 text-blue-700" />,
      title: 'Team Sharing',
      description: 'Deel wachtwoorden veilig binnen teams zonder ze te onthullen.',
    },
    {
      icon: <Key className="w-6 h-6 text-blue-700" />,
      title: 'Password Generator',
      description: 'Genereer sterke, unieke wachtwoorden automatisch.',
    },
    {
      icon: <Eye className="w-6 h-6 text-blue-700" />,
      title: 'Access Control',
      description: 'Bepaal wie toegang heeft tot welke wachtwoorden.',
    },
    {
      icon: <FileText className="w-6 h-6 text-blue-700" />,
      title: 'Veilige Notities',
      description: 'Sla gevoelige documenten en notities versleuteld op.',
    },
    {
      icon: <Shield className="w-6 h-6 text-blue-700" />,
      title: 'Audit Trails',
      description: 'Volg wie wanneer toegang heeft gehad tot wachtwoorden.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold mb-6">
                Enterprise Password Manager
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                Zoho Vault: Beveilig Uw Bedrijfswachtwoorden
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                Bescherm uw bedrijf tegen datalekken met Zoho Vault. Centraliseer en beveilig alle wachtwoorden 
                en gevoelige informatie van uw organisatie.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg" 
                    className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                  >
                    Vraag een Demo Aan
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                  </Button>
                </Link>
                
                <a href="tel:0638823471">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="border-2 border-blue-700 text-blue-700 hover:bg-blue-50 px-8 py-6 text-lg font-semibold rounded-lg transition-all duration-300"
                  >
                    Bel: 06 3882 3471
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1759489036027-44eac680741a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w0NjA2ODl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjB0ZWNobm9sb2d5fGVufDB8fHx8MTc3MzgxNzIzM3ww&ixlib=rb-4.1.0&q=85"
                alt="Security"
                className="rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What is Zoho Vault Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is Zoho Vault?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Zoho Vault is een enterprise password manager die bedrijven helpt om wachtwoorden en andere gevoelige 
                informatie veilig te beheren en te delen. Met militaire encryptie (AES-256) en zero-knowledge 
                architectuur zijn uw wachtwoorden maximaal beveiligd.
              </p>
              <p>
                Als Zoho Partner implementeert Guijs Consultancy Zoho Vault zo dat het aansluit op uw beveiligingsbeleid 
                en compliance eisen. Wij zorgen voor de juiste configuratie van toegangsrechten, policies en integraties 
                met uw bestaande IT infrastructuur.
              </p>
              <p>
                Zoho Vault elimineert zwakke wachtwoorden, voorkomt hergebruik van wachtwoorden en zorgt ervoor dat 
                wanneer medewerkers uit dienst gaan, zij geen toegang meer hebben tot bedrijfsaccounts. Dit verlaagt 
                het security risico aanzienlijk.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Krachtige Functies van Zoho Vault
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Enterprise-grade beveiliging voor uw bedrijfswachtwoorden.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1573164574511-73c773193279?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGNvbnN1bHRpbmd8ZW58MHx8fHwxNzczNzQ2NTMwfDA&ixlib=rb-4.1.0&q=85"
                alt="Security Consulting"
                className="rounded-2xl shadow-xl"
              />
            </div>

            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Voordelen van Zoho Vault
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Zoho Vault beschermt uw bedrijf tegen datalekken en maakt wachtwoordbeheer eenvoudig.
              </p>

              <ul className="space-y-4">
                {[
                  'Voorkom datalekken door zwakke wachtwoorden',
                  'Bespaar tijd met automatische inlog functionaliteit',
                  'Voldoe aan beveiligingseisen en compliance',
                  'Deel wachtwoorden veilig zonder ze te onthullen',
                  'Krijg controle over toegang tot bedrijfsaccounts',
                  'Bescherm bedrijfsdata ook na uitdiensttreding',
                ].map((benefit, index) => (
                  <li key={`item-${index}`} className="flex items-start space-x-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-lg">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Guijs Consultancy voor Zoho Vault?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Wij zorgen voor een veilige implementatie die aansluit op uw security policies.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Security Best Practices</h3>
                  <p className="text-gray-600">
                    Wij configureren Zoho Vault volgens security best practices en uw beveiligingsbeleid.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Toegangsbeleid</h3>
                  <p className="text-gray-600">
                    Wij richten role-based access control in voor optimale beveiliging.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Migratie Ondersteuning</h3>
                  <p className="text-gray-600">
                    Veilige migratie van bestaande wachtwoorden naar Zoho Vault.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">User Training</h3>
                  <p className="text-gray-600">
                    Training voor medewerkers om Zoho Vault effectief en veilig te gebruiken.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection 
        title="Klaar voor Betere Wachtwoordbeveiliging?"
        subtitle="Bescherm uw bedrijf met enterprise password management."
      />
    </div>
  );
};

export default ZohoVault;
